/**
 * SectorAwareSankeyBuilder.js
 * 
 * Creates sector-specific Sankey diagrams for financial data.
 * Detects company sector and builds appropriate Income Statement and Cash Flow visualizations.
 */

// Sector detection based on SIC codes and company characteristics
const SECTOR_PATTERNS = {
  banking: {
    keywords: ['bank', 'credit', 'mortgage', 'loan', 'financial services', 'paypal', 'stripe'],
    sicRanges: [[6000, 6999]], // SIC 6000-6999: Finance, Insurance, Real Estate
  },
  insurance: {
    keywords: ['insurance', 'assurance', 'reinsurance', 'underwriter'],
    sicRanges: [[6300, 6399]],
  },
  manufacturing: {
    keywords: ['manufacturing', 'automotive', 'industrial', 'equipment', 'machinery'],
    sicRanges: [[2000, 3999]], // SIC 2000-3999: Manufacturing
  },
  retail: {
    keywords: ['retail', 'store', 'shop', 'discount', 'mall', 'apparel', 'walmart', 'amazon'],
    sicRanges: [[5200, 5399]],
  },
  technology: {
    keywords: ['software', 'technology', 'tech', 'computer', 'saas', 'cloud', 'oracle', 'microsoft', 'apple', 'google', 'meta', 'nvidia'],
    sicRanges: [[7300, 7399]], // SIC 7300-7399: Business Services
  },
  healthcare: {
    keywords: ['pharmaceutical', 'pharma', 'biotech', 'medical', 'healthcare', 'hospital', 'drug'],
    sicRanges: [[2800, 2899]], // SIC 2800-2899: Chemicals and Pharmaceuticals
  },
  energy: {
    keywords: ['oil', 'gas', 'energy', 'petroleum', 'utility', 'electric'],
    sicRanges: [[1000, 1399], [4900, 4999]], // SIC 1000-1399: Mining, 4900-4999: Utilities
  },
  telecommunications: {
    keywords: ['telecom', 'communication', 'wireless', 'network'],
    sicRanges: [[4800, 4899]],
  },
  realestate: {
    keywords: ['reit', 'real estate', 'property', 'commercial real estate'],
    sicRanges: [[6700, 6799]],
  },
};

const toFiniteNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toSankeyLinkValue = (value) => {
  const parsed = toFiniteNumberOrNull(value);
  return parsed === null ? null : Math.abs(parsed);
};

const isNegativeValue = (value) => Number.isFinite(value) && value < 0;

const getOperatingResultNode = (operatingIncome) => {
  const isLoss = isNegativeValue(operatingIncome);
  return {
    name: isLoss ? 'Operating Loss' : 'Operating Income',
    value: operatingIncome,
    color: isLoss ? '#ef4444' : '#10b981',
    priorityLabel: true,
  };
};

const getNetIncomeResultNode = (netIncome) => {
  const isLoss = isNegativeValue(netIncome);
  return {
    name: isLoss ? 'Net Loss' : 'Net Income',
    value: netIncome,
    color: isLoss ? '#ef4444' : '#10b981',
    priorityLabel: true,
  };
};

/**
 * Detect sector based on company name and overview data
 * @param {string} companyName - Company name
 * @param {object} overview - Company overview object (optional)
 * @returns {string} - Sector name
 */
export const detectSector = (companyName = '', overview = {}) => {
  const nameUpper = (companyName || '').toUpperCase();
  
  // Try to match by SIC code first
  if (overview.sicCode) {
    const sicCode = parseInt(overview.sicCode);
    for (const [sector, config] of Object.entries(SECTOR_PATTERNS)) {
      for (const range of config.sicRanges) {
        if (sicCode >= range[0] && sicCode <= range[1]) {
          return sector;
        }
      }
    }
  }
  
  // Fall back to keyword matching
  for (const [sector, config] of Object.entries(SECTOR_PATTERNS)) {
    for (const keyword of config.keywords) {
      if (nameUpper.includes(keyword.toUpperCase())) {
        return sector;
      }
    }
  }
  
  // Default to generic if no match
  return 'generic';
};

/**
 * Build Income Statement Sankey for Manufacturing/Retail sector
 * Flow: Revenue → COGS → Gross Profit → OpEx → Operating Income → Interest/Taxes → Net Income
 */
const buildManufacturingIncomeStatement = (data) => {
  const {
    Revenue,
    'Cost of revenue': costOfRevenue,
    'Gross profits': grossProfits,
    'Total operating expenses': totalOperatingExpenses,
    'Operating Income': operatingIncome,
    'Interest Expense': interestExpense,
    'Income Before Tax': incomeBeforeTax,
    'Income Tax Expense': incomeTaxExpense,
    'Net Income': netIncome,
    currency = 'USD',
  } = data;

  const nodes = [];
  const links = [];
  const nodeMap = {};
  let nodeIndex = 0;

  // Revenue
  if (Revenue !== null) {
    nodeMap['Revenue'] = nodeIndex;
    nodes.push({ name: 'Revenue', value: Revenue, color: '#3b82f6', priorityLabel: true });
    nodeIndex++;
  }

  // Cost of Goods Sold
  if (costOfRevenue !== null) {
    nodeMap['COGS'] = nodeIndex;
    nodes.push({ name: 'Cost of Goods Sold', value: costOfRevenue, color: '#ef4444' });
    nodeIndex++;
    if ('Revenue' in nodeMap) {
      links.push({
        source: nodeMap['Revenue'],
        target: nodeMap['COGS'],
        value: toSankeyLinkValue(costOfRevenue),
      });
    }
  }

  // Gross Profit
  if (grossProfits !== null) {
    nodeMap['GP'] = nodeIndex;
    nodes.push({ name: 'Gross Profit', value: grossProfits, color: '#10b981' });
    nodeIndex++;
    if ('Revenue' in nodeMap) {
      links.push({
        source: nodeMap['Revenue'],
        target: nodeMap['GP'],
        value: toSankeyLinkValue(grossProfits),
      });
    }
  }

  // Operating Expenses
  if (totalOperatingExpenses !== null) {
    nodeMap['OpEx'] = nodeIndex;
    nodes.push({ name: 'Operating Expenses', value: totalOperatingExpenses, color: '#f59e0b' });
    nodeIndex++;
    if ('GP' in nodeMap) {
      links.push({
        source: nodeMap['GP'],
        target: nodeMap['OpEx'],
        value: toSankeyLinkValue(totalOperatingExpenses),
      });
    }
  }

  // Operating Income
  if (operatingIncome !== null) {
    nodeMap['OI'] = nodeIndex;
    nodes.push(getOperatingResultNode(operatingIncome));
    nodeIndex++;
    if ('OpEx' in nodeMap) {
      links.push({
        source: nodeMap['OpEx'],
        target: nodeMap['OI'],
        value: toSankeyLinkValue(operatingIncome),
      });
    } else if ('GP' in nodeMap) {
      links.push({
        source: nodeMap['GP'],
        target: nodeMap['OI'],
        value: toSankeyLinkValue(operatingIncome),
      });
    }
  }

  // Interest Expense
  if (interestExpense !== null && interestExpense > 0) {
    nodeMap['IE'] = nodeIndex;
    nodes.push({ name: 'Interest Expense', value: interestExpense, color: '#dc2626' });
    nodeIndex++;
    const sourceNode = 'OI' in nodeMap ? nodeMap['OI'] : nodeMap['GP'];
    if (sourceNode !== undefined) {
      links.push({
        source: nodeMap['IE'],
        target: sourceNode,
        value: toSankeyLinkValue(interestExpense),
      });
    }
  }

  // Income Before Tax
  if (incomeBeforeTax !== null) {
    nodeMap['IBT'] = nodeIndex;
    nodes.push({ name: 'Income Before Tax', value: incomeBeforeTax, color: '#8b5cf6' });
    nodeIndex++;
    const sourceNode = 'IE' in nodeMap ? nodeMap['IE'] : ('OI' in nodeMap ? nodeMap['OI'] : nodeMap['GP']);
    if (sourceNode !== undefined) {
      links.push({
        source: sourceNode,
        target: nodeMap['IBT'],
        value: toSankeyLinkValue(Math.abs(incomeBeforeTax)),
      });
    }
  }

  // Income Tax Expense
  if (incomeTaxExpense !== null && incomeTaxExpense > 0) {
    nodeMap['Tax'] = nodeIndex;
    nodes.push({ name: 'Income Tax', value: incomeTaxExpense, color: '#ef4444' });
    nodeIndex++;
    const sourceNode = 'IBT' in nodeMap ? nodeMap['IBT'] : ('OI' in nodeMap ? nodeMap['OI'] : nodeMap['GP']);
    if (sourceNode !== undefined) {
      links.push({
        source: nodeMap['Tax'],
        target: sourceNode,
        value: toSankeyLinkValue(incomeTaxExpense),
      });
    }
  }

  // Net Income
  if (netIncome !== null) {
    nodeMap['NI'] = nodeIndex;
    nodes.push(getNetIncomeResultNode(netIncome));
    nodeIndex++;
    const sourceNode = 'Tax' in nodeMap ? nodeMap['Tax'] : ('IBT' in nodeMap ? nodeMap['IBT'] : ('OI' in nodeMap ? nodeMap['OI'] : nodeMap['GP']));
    if (sourceNode !== undefined) {
      links.push({
        source: sourceNode,
        target: nodeMap['NI'],
        value: toSankeyLinkValue(netIncome),
      });
    }
  }

  return { nodes, links: links.filter((link) => Number.isFinite(link.value) && link.value > 0), currency };
};

/**
 * Build Income Statement Sankey for Banking sector
 * Flow: Operating Revenue → Operating Expenses → Operating Income → Interest/Other → Taxes → Net Income
 */
const buildBankingIncomeStatement = (data) => {
  const {
    Revenue,
    'Operating Income': operatingIncome,
    'Total operating expenses': totalOperatingExpenses,
    'Interest Expense': interestExpense,
    'Income Before Tax': incomeBeforeTax,
    'Income Tax Expense': incomeTaxExpense,
    'Net Income': netIncome,
    currency = 'USD',
  } = data;

  const nodes = [];
  const links = [];
  const nodeMap = {};
  let nodeIndex = 0;

  // Revenue
  if (Revenue !== null) {
    nodeMap['Revenue'] = nodeIndex;
    nodes.push({ name: 'Operating Revenue', value: Revenue, color: '#3b82f6', priorityLabel: true });
    nodeIndex++;
  }

  // Operating Expenses
  if (totalOperatingExpenses !== null) {
    nodeMap['OpEx'] = nodeIndex;
    nodes.push({ name: 'Operating Expenses', value: totalOperatingExpenses, color: '#f59e0b' });
    nodeIndex++;
    if ('Revenue' in nodeMap) {
      links.push({
        source: nodeMap['Revenue'],
        target: nodeMap['OpEx'],
        value: toSankeyLinkValue(totalOperatingExpenses),
      });
    }
  }

  // Operating Income
  if (operatingIncome !== null) {
    nodeMap['OI'] = nodeIndex;
    nodes.push(getOperatingResultNode(operatingIncome));
    nodeIndex++;
    if ('OpEx' in nodeMap) {
      links.push({
        source: nodeMap['OpEx'],
        target: nodeMap['OI'],
        value: toSankeyLinkValue(operatingIncome),
      });
    }
  }

  // Interest Expense (shows as outflow if present)
  if (interestExpense !== null && interestExpense > 0) {
    nodeMap['IE'] = nodeIndex;
    nodes.push({ name: 'Interest Expense', value: interestExpense, color: '#dc2626' });
    nodeIndex++;
    if ('OI' in nodeMap) {
      links.push({
        source: nodeMap['IE'],
        target: nodeMap['OI'],
        value: toSankeyLinkValue(interestExpense),
      });
    }
  }

  // Income Before Tax
  if (incomeBeforeTax !== null) {
    nodeMap['IBT'] = nodeIndex;
    nodes.push({ name: 'Income Before Tax', value: incomeBeforeTax, color: '#8b5cf6' });
    nodeIndex++;
    const lastOINode = 'IE' in nodeMap ? nodeMap['IE'] : nodeMap['OI'];
    if (lastOINode !== undefined) {
      links.push({
        source: lastOINode,
        target: nodeMap['IBT'],
        value: toSankeyLinkValue(Math.abs(incomeBeforeTax)),
      });
    }
  }

  // Income Tax Expense
  if (incomeTaxExpense !== null && incomeTaxExpense > 0) {
    nodeMap['Tax'] = nodeIndex;
    nodes.push({ name: 'Income Tax', value: incomeTaxExpense, color: '#ef4444' });
    nodeIndex++;
    const sourceNode = 'IBT' in nodeMap ? nodeMap['IBT'] : nodeMap['OI'];
    if (sourceNode !== undefined) {
      links.push({
        source: nodeMap['Tax'],
        target: sourceNode,
        value: toSankeyLinkValue(incomeTaxExpense),
      });
    }
  }

  // Net Income
  if (netIncome !== null) {
    nodeMap['NI'] = nodeIndex;
    nodes.push(getNetIncomeResultNode(netIncome));
    nodeIndex++;
    const sourceNode = 'Tax' in nodeMap ? nodeMap['Tax'] : ('IBT' in nodeMap ? nodeMap['IBT'] : nodeMap['OI']);
    if (sourceNode !== undefined) {
      links.push({
        source: sourceNode,
        target: nodeMap['NI'],
        value: toSankeyLinkValue(netIncome),
      });
    }
  }

  return { nodes, links: links.filter((link) => Number.isFinite(link.value) && link.value > 0), currency };
};

/**
 * Build Income Statement Sankey for Technology/SaaS sector
 * Flow: Revenue → Cost of Revenue → Gross Profit → R&D → Operating Income → Interest/Taxes → Net Income
 */
const buildTechnologyIncomeStatement = (data) => {
  const {
    Revenue,
    'Cost of revenue': costOfRevenue,
    'Gross profits': grossProfits,
    'Total operating expenses': totalOperatingExpenses,
    'Operating Income': operatingIncome,
    'Interest Expense': interestExpense,
    'Income Before Tax': incomeBeforeTax,
    'Income Tax Expense': incomeTaxExpense,
    'Net Income': netIncome,
    currency = 'USD',
  } = data;

  const nodes = [];
  const links = [];
  const nodeMap = {};
  let nodeIndex = 0;

  // Revenue
  if (Revenue !== null) {
    nodeMap['Revenue'] = nodeIndex;
    nodes.push({ name: 'Revenue', value: Revenue, color: '#3b82f6', priorityLabel: true });
    nodeIndex++;
  }

  // Cost of Revenue
  if (costOfRevenue !== null) {
    nodeMap['COR'] = nodeIndex;
    nodes.push({ name: 'Cost of Revenue', value: costOfRevenue, color: '#ef4444' });
    nodeIndex++;
    if ('Revenue' in nodeMap) {
      links.push({
        source: nodeMap['Revenue'],
        target: nodeMap['COR'],
        value: toSankeyLinkValue(costOfRevenue),
      });
    }
  }

  // Gross Profit
  if (grossProfits !== null) {
    nodeMap['GP'] = nodeIndex;
    nodes.push({ name: 'Gross Profit', value: grossProfits, color: '#10b981' });
    nodeIndex++;
    if ('Revenue' in nodeMap) {
      links.push({
        source: nodeMap['Revenue'],
        target: nodeMap['GP'],
        value: toSankeyLinkValue(grossProfits),
      });
    }
  }

  // Operating Expenses (R&D + S&M + G&A)
  if (totalOperatingExpenses !== null) {
    nodeMap['OpEx'] = nodeIndex;
    nodes.push({ name: 'R&D + S&M + G&A', value: totalOperatingExpenses, color: '#f59e0b' });
    nodeIndex++;
    if ('GP' in nodeMap) {
      links.push({
        source: nodeMap['GP'],
        target: nodeMap['OpEx'],
        value: toSankeyLinkValue(totalOperatingExpenses),
      });
    }
  }

  // Operating Income
  if (operatingIncome !== null) {
    nodeMap['OI'] = nodeIndex;
    nodes.push(getOperatingResultNode(operatingIncome));
    nodeIndex++;
    if ('OpEx' in nodeMap && !isNegativeValue(operatingIncome)) {
      links.push({
        source: nodeMap['OpEx'],
        target: nodeMap['OI'],
        value: toSankeyLinkValue(operatingIncome),
      });
    } else if ('GP' in nodeMap && !isNegativeValue(operatingIncome)) {
      links.push({
        source: nodeMap['GP'],
        target: nodeMap['OI'],
        value: toSankeyLinkValue(operatingIncome),
      });
    }
  }

  // Interest Expense
  if (interestExpense !== null && interestExpense > 0) {
    nodeMap['IE'] = nodeIndex;
    nodes.push({ name: 'Interest Expense', value: interestExpense, color: '#dc2626' });
    nodeIndex++;
    if ('OI' in nodeMap) {
      links.push({
        source: nodeMap['IE'],
        target: nodeMap['OI'],
        value: toSankeyLinkValue(interestExpense),
      });
    }
  }

  // Income Before Tax
  if (incomeBeforeTax !== null) {
    nodeMap['IBT'] = nodeIndex;
    nodes.push({ name: 'Income Before Tax', value: incomeBeforeTax, color: '#8b5cf6' });
    nodeIndex++;
    const sourceNode = 'IE' in nodeMap ? nodeMap['IE'] : nodeMap['OI'];
    if (sourceNode !== undefined) {
      links.push({
        source: sourceNode,
        target: nodeMap['IBT'],
        value: toSankeyLinkValue(Math.abs(incomeBeforeTax)),
      });
    }
  }

  // Income Tax Expense
  if (incomeTaxExpense !== null && incomeTaxExpense > 0) {
    nodeMap['Tax'] = nodeIndex;
    nodes.push({ name: 'Income Tax', value: incomeTaxExpense, color: '#ef4444' });
    nodeIndex++;
    const sourceNode = 'IBT' in nodeMap ? nodeMap['IBT'] : nodeMap['OI'];
    if (sourceNode !== undefined) {
      links.push({
        source: nodeMap['Tax'],
        target: sourceNode,
        value: toSankeyLinkValue(incomeTaxExpense),
      });
    }
  }

  // Net Income
  if (netIncome !== null) {
    nodeMap['NI'] = nodeIndex;
    nodes.push(getNetIncomeResultNode(netIncome));
    nodeIndex++;
    const sourceNode = 'Tax' in nodeMap ? nodeMap['Tax'] : ('IBT' in nodeMap ? nodeMap['IBT'] : nodeMap['OI']);
    if (sourceNode !== undefined) {
      links.push({
        source: sourceNode,
        target: nodeMap['NI'],
        value: toSankeyLinkValue(netIncome),
      });
    }
  }

  return { nodes, links: links.filter((link) => Number.isFinite(link.value) && link.value > 0), currency };
};

/**
 * Build generic Income Statement Sankey
 */
const buildGenericIncomeStatement = (data) => {
  const {
    Revenue,
    'Cost of revenue': costOfRevenue,
    'Gross profits': grossProfits,
    'Total operating expenses': totalOperatingExpenses,
    'Operating Income': operatingIncome,
    'Interest Expense': interestExpense,
    'Income Before Tax': incomeBeforeTax,
    'Income Tax Expense': incomeTaxExpense,
    'Net Income': netIncome,
    currency = 'USD',
  } = data;

  const nodes = [];
  const links = [];
  let nodeIndex = 0;
  const nodeMap = {};

  // Add Revenue node
  if (Revenue !== null) {
    nodeMap['Revenue'] = nodeIndex;
    nodes.push({
      name: 'Revenue',
      value: Revenue,
      color: '#3b82f6',
      priorityLabel: true,
    });
    nodeIndex++;
  }

  // Add COGS node
  if (costOfRevenue !== null) {
    nodeMap['COGS'] = nodeIndex;
    nodes.push({
      name: 'Cost of Revenue',
      value: costOfRevenue,
      color: '#ef4444',
    });
    nodeIndex++;
    if ('Revenue' in nodeMap) {
      links.push({
        source: nodeMap['Revenue'],
        target: nodeMap['COGS'],
        value: toSankeyLinkValue(costOfRevenue),
      });
    }
  }

  // Add Gross Profit node
  if (grossProfits !== null) {
    nodeMap['GP'] = nodeIndex;
    nodes.push({
      name: 'Gross Profit',
      value: grossProfits,
      color: '#10b981',
    });
    nodeIndex++;
    if ('Revenue' in nodeMap) {
      links.push({
        source: nodeMap['Revenue'],
        target: nodeMap['GP'],
        value: toSankeyLinkValue(grossProfits),
      });
    }
  }

  // Add OpEx node
  if (totalOperatingExpenses !== null) {
    nodeMap['OpEx'] = nodeIndex;
    nodes.push({
      name: 'Operating Expenses',
      value: totalOperatingExpenses,
      color: '#ef4444',
    });
    nodeIndex++;
    if ('GP' in nodeMap) {
      links.push({
        source: nodeMap['GP'],
        target: nodeMap['OpEx'],
        value: toSankeyLinkValue(totalOperatingExpenses),
      });
    }
  }

  // Add Operating Income node
  if (operatingIncome !== null) {
    nodeMap['OI'] = nodeIndex;
    nodes.push(getOperatingResultNode(operatingIncome));
    nodeIndex++;
    if ('OpEx' in nodeMap) {
      links.push({
        source: nodeMap['OpEx'],
        target: nodeMap['OI'],
        value: toSankeyLinkValue(operatingIncome),
      });
    } else if ('GP' in nodeMap) {
      links.push({
        source: nodeMap['GP'],
        target: nodeMap['OI'],
        value: toSankeyLinkValue(operatingIncome),
      });
    }
  }

  // Add Interest Expense node
  if (interestExpense !== null) {
    nodeMap['IE'] = nodeIndex;
    const isInterestIncome = Number(interestExpense) < 0;
    nodes.push({
      name: isInterestIncome ? 'Interest Income' : 'Interest Expense',
      value: Math.abs(interestExpense),
      color: isInterestIncome ? '#10b981' : '#ef4444',
    });
    nodeIndex++;
    const sourceNode = 'OI' in nodeMap ? nodeMap['OI'] : nodeMap['GP'];
    if (sourceNode !== undefined) {
      links.push({
        source: nodeMap['IE'],
        target: sourceNode,
        value: toSankeyLinkValue(Math.abs(interestExpense)),
      });
    }
  }

  // Add Income Before Tax node
  if (incomeBeforeTax !== null) {
    nodeMap['IBT'] = nodeIndex;
    nodes.push({
      name: 'Income Before Tax',
      value: incomeBeforeTax,
      color: '#8b5cf6',
    });
    nodeIndex++;
    const sourceNode = 'IE' in nodeMap ? nodeMap['IE'] : ('OI' in nodeMap ? nodeMap['OI'] : nodeMap['GP']);
    if (sourceNode !== undefined) {
      links.push({
        source: sourceNode,
        target: nodeMap['IBT'],
        value: toSankeyLinkValue(Math.abs(incomeBeforeTax)),
      });
    }
  }

  // Add Income Tax Expense node
  if (incomeTaxExpense !== null) {
    nodeMap['Tax'] = nodeIndex;
    const isTaxBenefit = Number(incomeTaxExpense) < 0;
    nodes.push({
      name: isTaxBenefit ? 'Income Tax Benefit' : 'Income Tax',
      value: Math.abs(incomeTaxExpense),
      color: isTaxBenefit ? '#10b981' : '#ef4444',
    });
    nodeIndex++;
    const sourceNode = 'IBT' in nodeMap ? nodeMap['IBT'] : ('OI' in nodeMap ? nodeMap['OI'] : nodeMap['GP']);
    if (sourceNode !== undefined) {
      links.push({
        source: nodeMap['Tax'],
        target: sourceNode,
        value: toSankeyLinkValue(Math.abs(incomeTaxExpense)),
      });
    }
  }

  // Add Net Income node
  if (netIncome !== null) {
    nodeMap['NI'] = nodeIndex;
    nodes.push(getNetIncomeResultNode(netIncome));
    nodeIndex++;
    const sourceNode = 'Tax' in nodeMap ? nodeMap['Tax'] : ('IBT' in nodeMap ? nodeMap['IBT'] : ('OI' in nodeMap ? nodeMap['OI'] : nodeMap['GP']));
    if (sourceNode !== undefined) {
      links.push({
        source: sourceNode,
        target: nodeMap['NI'],
        value: toSankeyLinkValue(netIncome),
      });
    }
  }

  return {
    nodes,
    links: links.filter((link) => Number.isFinite(link.value) && link.value > 0),
    currency,
  };
};

/**
 * Build Income Statement Sankey based on sector
 */
export const buildIncomeStatementSankey = (data, sector = 'generic') => {
  if (!data) return null;

  // Filter null/undefined values and convert to proper numbers
  const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
    if (key === 'currency') {
      acc[key] = value;
    } else if (value !== null && value !== undefined && value !== '') {
      acc[key] = Number(value);
    } else {
      acc[key] = null;
    }
    return acc;
  }, {});

  return buildGenericIncomeStatement(cleanData);
};

/**
 * Build Cash Flow Sankey for all sectors
 * Core flow: Operating CF → Investing CF → Financing CF → Net Change in Cash
 */
export const buildCashFlowSankey = (data, sector = 'generic') => {
  if (!data) return null;

  const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
    if (key === 'currency') {
      acc[key] = value;
    } else {
      acc[key] = toFiniteNumberOrNull(value);
    }
    return acc;
  }, {});

  const {
    operating_cashflow,
    net_income,
    capital_expenditures,
    cashflow_from_investment,
    cashflow_from_financing,
    change_in_cash_and_cash_equivalents,
    currency = 'USD',
  } = cleanData;

  // Sector-specific styling
  const sectorColors = {
    banking: { operating: '#06b6d4', investing: '#8b5cf6', financing: '#ec4899' },
    insurance: { operating: '#06b6d4', investing: '#8b5cf6', financing: '#ec4899' },
    manufacturing: { operating: '#3b82f6', investing: '#f59e0b', financing: '#10b981' },
    retail: { operating: '#3b82f6', investing: '#f59e0b', financing: '#10b981' },
    technology: { operating: '#3b82f6', investing: '#8b5cf6', financing: '#10b981' },
    healthcare: { operating: '#3b82f6', investing: '#06b6d4', financing: '#10b981' },
    energy: { operating: '#3b82f6', investing: '#f59e0b', financing: '#10b981' },
    telecommunications: { operating: '#3b82f6', investing: '#8b5cf6', financing: '#10b981' },
    realestate: { operating: '#3b82f6', investing: '#f59e0b', financing: '#10b981' },
    generic: { operating: '#3b82f6', investing: '#f59e0b', financing: '#10b981' },
  };

  const colors = sectorColors[sector] || sectorColors.generic;

  const nodes = [];
  const links = [];
  const nodeMap = {};

  // Add source node
  if (net_income !== null) {
    nodeMap['NetIncome'] = nodes.length;
    nodes.push(getNetIncomeResultNode(net_income));
  }

  // Operating Cash Flow
  if (operating_cashflow !== null) {
    nodeMap['OCF'] = nodes.length;
    nodes.push({
      name: 'Operating Cash Flow',
      value: operating_cashflow,
      color: colors.operating,
      priorityLabel: true,
    });
    if ('NetIncome' in nodeMap) {
      links.push({
        source: nodeMap['NetIncome'],
        target: nodeMap['OCF'],
        value: operating_cashflow,
      });
    }
  }

  // Investing Cash Flow
  if (cashflow_from_investment !== null) {
    nodeMap['ICF'] = nodes.length;
    nodes.push({
      name: 'Investing Cash Flow',
      value: cashflow_from_investment,
      color: colors.investing,
    });
  }

  // Financing Cash Flow
  if (cashflow_from_financing !== null) {
    nodeMap['FCF'] = nodes.length;
    nodes.push({
      name: 'Financing Cash Flow',
      value: cashflow_from_financing,
      color: colors.financing,
    });
  }

  // Capex
  if (capital_expenditures !== null) {
    nodeMap['CapEx'] = nodes.length;
    nodes.push({
      name: 'Capital Expenditures',
      value: capital_expenditures,
      color: '#ef4444',
    });
    if ('ICF' in nodeMap) {
      links.push({
        source: nodeMap['ICF'],
        target: nodeMap['CapEx'],
        value: Math.abs(capital_expenditures),
      });
    }
  }

  // Net Change in Cash
  if (change_in_cash_and_cash_equivalents !== null) {
    nodeMap['NetChange'] = nodes.length;
    const netChangeColor =
      change_in_cash_and_cash_equivalents >= 0 ? '#10b981' : '#ef4444';
    nodes.push({
      name: 'Net Change in Cash',
      value: change_in_cash_and_cash_equivalents,
      color: netChangeColor,
      priorityLabel: true,
    });
    if ('OCF' in nodeMap) {
      links.push({
        source: nodeMap['OCF'],
        target: nodeMap['NetChange'],
        value: Math.abs(change_in_cash_and_cash_equivalents),
      });
    }
    if ('ICF' in nodeMap) {
      links.push({
        source: nodeMap['ICF'],
        target: nodeMap['NetChange'],
        value: Math.abs(cashflow_from_investment),
      });
    }
    if ('FCF' in nodeMap) {
      links.push({
        source: nodeMap['FCF'],
        target: nodeMap['NetChange'],
        value: Math.abs(cashflow_from_financing),
      });
    }
  }

  return { nodes, links, currency };
};

